/**
 * White-background chroma key for LitSquad accessory art.
 *
 * Legacy LitSquad accessory thumbnails (e.g. the bear dunce cap) are stored
 * as JPEGs with a solid white background. When layered on a character for
 * the avatar editor, that white square covers the character beneath. These
 * helpers run an edge-flood chroma key: pixels reachable from the canvas
 * border via 4-connectivity that are within `WHITE_THRESHOLD` of pure white
 * are zeroed out. Internal white (e.g. the "D" letter inside the cap) is
 * preserved because the flood-fill can't cross the cap's purple silhouette
 * to reach it. PNGs that already have a transparent background are
 * effectively no-ops because their border pixels have alpha 0 and aren't
 * re-considered as solid backgrounds.
 */

/** Pixels at or above this brightness on every channel are treated as background. */
const WHITE_THRESHOLD = 240;

/**
 * Subset of the DOM `ImageData` interface we depend on. Pure JS code paths
 * (and tests) can pass any object with `data`, `width`, `height` — they
 * don't need a real DOM ImageData.
 */
export interface ImageDataLike {
  data: Uint8ClampedArray;
  width: number;
  height: number;
}

/**
 * Mutates `image.data` in place: any pixel within `WHITE_THRESHOLD` of pure
 * white that is reachable (via 4-connectivity) from the canvas border is
 * given alpha 0. Returns the same object for chainability.
 *
 * Pure pixel logic — no DOM dependencies — so the web editor (over a canvas
 * `ImageData`) and the server renderer (over decoded RGBA bytes) key the art
 * identically.
 */
export function keyOutWhiteBackgroundPixels<T extends ImageDataLike>(image: T): T {
  const { data, width, height } = image;
  const total = width * height;
  if (total === 0) return image;

  const isWhiteish = (idx: number): boolean => {
    const base = idx * 4;
    return (
      data[base] >= WHITE_THRESHOLD &&
      data[base + 1] >= WHITE_THRESHOLD &&
      data[base + 2] >= WHITE_THRESHOLD &&
      data[base + 3] !== 0 // already-transparent pixels don't need re-marking
    );
  };

  // BFS from every border pixel that is white-ish. Use a head-pointer queue
  // so dequeue is O(1) — Array.shift() on a 1024×1024 worst case would be
  // catastrophically slow.
  const isBackground = new Uint8Array(total);
  const queue: number[] = [];
  let head = 0;

  const seed = (idx: number): void => {
    if (!isBackground[idx] && isWhiteish(idx)) {
      isBackground[idx] = 1;
      queue.push(idx);
    }
  };

  for (let x = 0; x < width; x++) {
    seed(x); // top row
    seed((height - 1) * width + x); // bottom row
  }
  for (let y = 0; y < height; y++) {
    seed(y * width); // left column
    seed(y * width + (width - 1)); // right column
  }

  while (head < queue.length) {
    const idx = queue[head++];
    const x = idx % width;
    const y = (idx - x) / width;

    if (x > 0) seed(idx - 1);
    if (x < width - 1) seed(idx + 1);
    if (y > 0) seed(idx - width);
    if (y < height - 1) seed(idx + width);
  }

  // Zero alpha on every flagged background pixel.
  for (let i = 0; i < total; i++) {
    if (isBackground[i]) data[i * 4 + 3] = 0;
  }

  return image;
}
